import { useState } from "react";
import { UserPlus, X, Check } from "lucide-react";

const EMPTY = { first: "", last: "", email: "", phone: "", shift: "Morning" };

/**
 * Walk-in Registration — touch-first, responsive form dialog.
 * Presentation-only in this milestone: it validates and shows a completion
 * state but does NOT persist to the backend (no data-model change).
 */
export function WalkInRegistration() {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  const close = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm(EMPTY); setErrors({}); }, 200);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const err = {};
    if (!form.first.trim()) err.first = "Required";
    if (!form.last.trim()) err.last = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = "Enter a valid email";
    if (form.phone.replace(/\D/g, "").length < 10) err.phone = "Enter a valid phone";
    setErrors(err);
    if (Object.keys(err).length === 0) setDone(true);
  };

  return (
    <>
      <button className="btn sm primary" data-testid="walk-in-registration-btn" onClick={() => setOpen(true)}>
        <UserPlus size={16} /> <span className="btn-txt">Walk-in Registration</span>
      </button>

      {open && (
        <div className="modal-scrim" onClick={close} data-testid="walk-in-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-head">
              <h2>{done ? "Registration complete" : "Walk-in Registration"}</h2>
              <button className="x" onClick={close} aria-label="Close" data-testid="walk-in-close"><X size={20} /></button>
            </div>

            {done ? (
              <div className="modal-body">
                <div className="reg-success" data-testid="walk-in-success">
                  <div className="ic"><Check size={28} /></div>
                  <h3>{form.first} {form.last} registered</h3>
                  <p>Added to <strong>Interview Pending</strong> in the active candidate pool. A registration receipt has been queued.</p>
                </div>
                <div className="modal-foot">
                  <button className="btn primary" onClick={close} data-testid="walk-in-done">Done</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="modal-body">
                  <p className="modal-intro">Register a candidate who has arrived in person. They will join Interview Pending, exactly like a public QR registration.</p>
                  <div className="form-grid">
                    <div className="field">
                      <label htmlFor="wi-first">First name</label>
                      <input id="wi-first" value={form.first} onChange={set("first")} data-testid="walk-in-first" autoComplete="given-name" />
                      {errors.first && <span className="err">{errors.first}</span>}
                    </div>
                    <div className="field">
                      <label htmlFor="wi-last">Last name</label>
                      <input id="wi-last" value={form.last} onChange={set("last")} data-testid="walk-in-last" autoComplete="family-name" />
                      {errors.last && <span className="err">{errors.last}</span>}
                    </div>
                    <div className="field full">
                      <label htmlFor="wi-email">Email</label>
                      <input id="wi-email" type="email" inputMode="email" value={form.email} onChange={set("email")} data-testid="walk-in-email" autoComplete="email" />
                      {errors.email && <span className="err">{errors.email}</span>}
                    </div>
                    <div className="field">
                      <label htmlFor="wi-phone">Phone</label>
                      <input id="wi-phone" type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="walk-in-phone" autoComplete="tel" />
                      {errors.phone && <span className="err">{errors.phone}</span>}
                    </div>
                    <div className="field">
                      <label htmlFor="wi-shift">Preferred shift</label>
                      <select id="wi-shift" value={form.shift} onChange={set("shift")} data-testid="walk-in-shift">
                        <option>Morning</option>
                        <option>Evening</option>
                        <option>Any shift</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-foot">
                  <button type="button" className="btn ghost" onClick={close} data-testid="walk-in-cancel">Cancel</button>
                  <button type="submit" className="btn primary" data-testid="walk-in-submit">Register candidate</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
